import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Box, Button, Group, Loader, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Locale } from "@iso27001/shared";
import { docsApi, type DocumentDetail } from "../../api/docs.js";
import { ApiError } from "../../api/client.js";
import { DocFrame } from "./DocFrame.js";
import { formatVersionDate } from "./format-version-date.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { bannerFor } from "./translation-banner.js";
import { navigationTargetFor } from "./frame-navigation.js";

const CLASSIFICATION_COLOR: Record<DocumentDetail["classification"], string> = {
  "öffentlich": "green",
  intern: "blue",
  vertraulich: "red",
};

export function DocumentPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const uiLocale = i18n.language as Locale;
  const { label } = useEnumLabel();

  // Per-document and transient: keyed into the query below, and reset when
  // the slug changes. A sticky override would be a second language setting
  // competing with the header switcher.
  const [override, setOverride] = useState<Locale | null>(null);
  useEffect(() => setOverride(null), [slug]);
  const requested = override ?? uiLocale;

  const { data, isLoading, error } = useQuery({
    queryKey: ["doc", slug, requested],
    queryFn: () => docsApi.item(slug, requested),
    retry: false,
  });

  const handleNavigate = useCallback(
    (next: string) => {
      const target = navigationTargetFor(next, slug, data?.translation.slug ?? null);
      if (target) navigate(`/dokumentation/${target}`, { replace: true });
    },
    [navigate, slug, data?.translation.slug],
  );

  if (isLoading) return <Loader />;
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <Alert color={notFound ? "yellow" : "red"}>
        {notFound ? t("docs.document.notFound") : t("docs.document.loadError")}
      </Alert>
    );
  }
  if (!data) return null;

  const banner = bannerFor(requested, data.translation);

  return (
    <Stack gap="xs" h="calc(100vh - 96px)">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Group gap="xs" align="center">
            <Title order={3}>{data.title}</Title>
            <SegmentedControl
              size="xs"
              value={data.translation.rendered}
              onChange={(v) => setOverride(v as Locale)}
              data={[{ label: "DE", value: "de" }, { label: "EN", value: "en" }]}
              // Disabled only once we have actually asked for English and
              // learned it does not exist — not merely because we have not
              // asked yet (the default German request never even checks, so
              // `available` reads false regardless of whether a translation
              // exists, and disabling on that alone would lock the control
              // for every document a German-locale reader opens).
              disabled={requested === "en" && !data.translation.available}
            />
          </Group>
          <Group gap="xs">
            <Badge color={CLASSIFICATION_COLOR[data.classification]} variant="light">
              {label("clearance", data.classification)}
            </Badge>
            {data.sectionTitle && <Text size="sm" c="dimmed">{data.sectionTitle}</Text>}
            {data.version && <Text size="sm" c="dimmed">{t("docs.field.version")} {data.version}</Text>}
            {formatVersionDate(data.versionDate, uiLocale) && (
              <Text size="sm" c="dimmed">{t("docs.field.asOf")} {formatVersionDate(data.versionDate, uiLocale)}</Text>
            )}
            {data.ersteller && <Text size="sm" c="dimmed">{t("docs.field.author")} {data.ersteller}</Text>}
            {data.freigabe && <Text size="sm" c="dimmed">{t("docs.field.approval")} {data.freigabe}</Text>}
          </Group>
        </Stack>
        {data.bundleUrl && (
          <Button
            component="a"
            href={data.bundleUrl}
            target="_blank"
            rel="noreferrer"
            variant="light"
            leftSection={<IconExternalLink size={16} />}
          >
            {t("docs.document.openInNewTab")}
          </Button>
        )}
      </Group>

      {banner?.kind === "germanOnly" && (
        <Alert color="yellow">{t("docs.document.germanOnly")}</Alert>
      )}
      {banner?.kind === "stale" && (
        <Alert color="yellow" title={t("docs.document.staleTitle")}>
          {t("docs.document.staleBody", { translated: banner.translated, original: banner.original })}
          <Button variant="subtle" size="compact-sm" onClick={() => setOverride("de")}>
            {t("docs.document.readGerman")}
          </Button>
        </Alert>
      )}

      {data.bundleUrl ? (
        <Box style={{ flex: 1, minHeight: 0, border: "1px solid var(--mantine-color-default-border)", borderRadius: 4 }}>
          <DocFrame src={data.bundleUrl} onNavigate={handleNavigate} />
        </Box>
      ) : data.translation.available ? (
        // The translation record exists — locale=en resolved a title/version —
        // but this instance's bundle never got the page (not yet built, or
        // built into a different instance; see pageExistsInBundle's doc
        // comment in routes.ts). The German rendering is unaffected by that,
        // so this is not the same "nothing built at all" situation
        // noBundleWarning describes, and the reader should be told they can
        // recover via the DE/EN toggle above, which stays enabled for exactly
        // this reason.
        <Alert color="yellow">
          {t("docs.document.translationNotBuilt")}
          <Button variant="subtle" size="compact-sm" onClick={() => setOverride("de")}>
            {t("docs.document.readGerman")}
          </Button>
        </Alert>
      ) : (
        <Alert color="yellow">
          {t("docs.document.noBundleWarning")}
        </Alert>
      )}
    </Stack>
  );
}
